import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminSettings from "./pages/AdminSettings";
import AdminCRM from "./pages/AdminCRM";
import AdminAffiliates from "./pages/AdminAffiliates";
import AdminAcademies from "./pages/AdminAcademies";
import AdminSupport from "./pages/AdminSupport";
import Login from "./pages/Login";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ScrollToTop from "@/components/ScrollToTop";
import { ThemeToggle } from "@/components/ThemeToggle";
import AuthenticatedWidgets from "@/components/AuthenticatedWidgets";
import { usePageTracking } from "@/hooks/usePageTracking";
import { HelmetProvider } from "react-helmet-async";
import { TranslationProvider } from "@/contexts/TranslationContext";
import Index from "./pages/Index";
import Apply from "./pages/Apply";
import About from "./pages/About";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminContent from "./pages/AdminContent";

import Academy from "./pages/Academy";
import Module from "./pages/Module";
import Class from "./pages/Class";
import Profile from "./pages/Profile";
import Affiliates from "./pages/Affiliates";
import PaymentSuccess from "./pages/PaymentSuccess";
import ResetPassword from "./pages/ResetPassword";
import SetPassword from "./pages/SetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import NotFound from "./pages/NotFound";
import Links from "./pages/Links";
import Leaderboard from "./pages/Leaderboard";
import AITools from "./pages/AITools";
import AdminAITools from "./pages/AdminAITools";
import LaunchKit from "./pages/LaunchKit";

// Initialize React Query client
const queryClient = new QueryClient();

const useMetaPixel = () => {
  useEffect(() => {
    if ((window as any).fbq) return; // Already loaded
    
    const f = window as any;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    
    f.fbq('init', '933872596268826');
    f.fbq('init', '778185344689672');
    f.fbq('track', 'PageView');
  }, []);
};

const App = () => {
  useMetaPixel();
  
  return (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TranslationProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <PageTracker />
              <AuthenticatedWidgets />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/about" element={<About />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/affiliates" element={<Affiliates />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/crm" element={<AdminCRM />} />
              <Route path="/admin/affiliates" element={<AdminAffiliates />} />
              <Route path="/admin/academies" element={<AdminAcademies />} />
              <Route path="/admin/support" element={<AdminSupport />} />
              <Route path="/admin/ai-tools" element={<AdminAITools />} />
              <Route path="/academy/:academyId" element={<Academy />} />
              <Route path="/academy/:academyId/module/:moduleId" element={<Module />} />
              <Route path="/academy/:academyId/module/:moduleId/class/:classNumber" element={<Class />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/refunds" element={<RefundPolicy />} />
              <Route path="/links" element={<Links />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/ai-tools" element={<AITools />} />
              <Route path="/launch-kit" element={<LaunchKit />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </TranslationProvider>
     </AuthProvider>
   </QueryClientProvider>
   </ThemeProvider>
   </HelmetProvider>
  );
};

const PageTracker = () => {
  usePageTracking();
  return null;
};

export default App;
